<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { defineEmits, reactive, ref } from 'vue';

import CardComponent from '@/components/ui/CardComponent.vue';

import type { DuplicateReviewNoteRequest } from '@/types/services/identityDuplicateReview/identityDuplicateReview.type.ts';

const { t } = useI18n();
const emits = defineEmits(['closeModal']);

const props = defineProps<{
  // 'clear' = homonym, 'confirm' = real duplicate (the account gets deactivated)
  mode: 'clear' | 'confirm';
  userName: string;
}>();

const formValid = ref<boolean>(false);
const form = reactive<DuplicateReviewNoteRequest>({ note: '' });

const noteRules = [
  (value: string) => {
    const length = (value ?? '').trim().length
    return (length >= 5 && length <= 1000) || t('components.resolveDuplicateReview.noteLength')
  }
];

function submit(): void {
  emits('closeModal', { note: form.note.trim() })
}
</script>

<template>
  <v-form class="w-100 d-flex align-center">
    <CardComponent with-actions>
      <template #title>
        {{ t(`components.resolveDuplicateReview.${props.mode}.title`) }}
      </template>
      <template #item>
        <v-alert
          v-if="props.mode === 'confirm'"
          class="mb-4"
          color="warning"
          variant="outlined"
        >
          {{ t('components.resolveDuplicateReview.confirm.warning', { user_name: props.userName }) }}
        </v-alert>
        <p class="mb-4">{{ t(`components.resolveDuplicateReview.${props.mode}.message`, { user_name: props.userName }) }}</p>
        <v-form v-model="formValid">
          <v-textarea
            v-model="form.note"
            :label="t('components.resolveDuplicateReview.note')"
            :rules="noteRules"
            counter="1000"
            hide-details="auto"
            rows="4"
          />
        </v-form>
      </template>
      <template #actions>
        <div class="d-flex flex-row justify-space-between ga-4 mt-2">
          <v-btn variant="outlined" @click.prevent="emits('closeModal', false)">
            {{ t('common.buttons.cancel') }}
          </v-btn>
          <v-btn
            :color="props.mode === 'confirm' ? 'error' : 'primary'"
            :disabled="!formValid"
            type="submit"
            variant="flat"
            @click.prevent="submit"
          >
            {{ t('common.buttons.validate') }}
          </v-btn>
        </div>
      </template>
    </CardComponent>
  </v-form>
</template>
